import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {StrictMode, useEffect, useRef} from "react";
import {createRoot} from "react-dom/client";
import {BrowserRouter, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {trackFunnel} from "@/lib/funnel";
import {SubscribePage} from "@/routes/SubscribePage";
import subscribeStyles from "@/styles/subscribe.css?inline";
import dialogStyles from "@/styles/pricing-checkout.css?inline";

type PricingCheckoutOption = "monthly" | "trial";

const triggers = Array.from(
  document.querySelectorAll<HTMLButtonElement>("[data-pricing-checkout]"),
);
const container = document.getElementById("pricing-checkout-root");

function PricingCheckout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dialog = useRef<HTMLDialogElement>(null);
  const lastTrigger = useRef<HTMLButtonElement | null>(null);
  const previousOption = useRef<PricingCheckoutOption | null>(null);
  const trackedOpen = useRef<string | null>(null);
  const requestedCheckout = searchParams.get("checkout");
  const checkoutOption: PricingCheckoutOption | null =
    requestedCheckout === "monthly" || requestedCheckout === "trial"
      ? requestedCheckout
      : null;

  const close = () => {
    if (location.state?.pricingCheckoutOpened) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, {replace: true, preventScrollReset: true});
  };

  useEffect(() => {
    const listeners = triggers.map((trigger) => {
      const show = () => {
        const option = trigger.dataset.pricingCheckout;
        if (option !== "monthly" && option !== "trial") return;
        lastTrigger.current = trigger;
        const next = new URLSearchParams(window.location.search);
        next.set("checkout", option);
        setSearchParams(next, {preventScrollReset: true, state: {pricingCheckoutOpened: true}});
      };
      trigger.addEventListener("click", show);
      return {trigger, show};
    });
    return () => {
      listeners.forEach(({trigger, show}) => trigger.removeEventListener("click", show));
    };
  }, [setSearchParams]);

  useEffect(() => {
    const element = dialog.current;
    if (!checkoutOption) {
      if (previousOption.current) {
        const trigger = lastTrigger.current || triggers.find((item) => item.dataset.pricingCheckout === previousOption.current);
        trigger?.focus({preventScroll: true});
      }
      previousOption.current = null;
      return;
    }
    if (!element) return;
    previousOption.current = checkoutOption;
    element.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      element.close();
      document.body.style.overflow = overflow;
    };
  }, [checkoutOption]);

  useEffect(() => {
    const trackOpen = () => {
      if (checkoutOption !== "trial" || trackedOpen.current === location.key) return;
      if (trackFunnel("trial_checkout_open", {source: "pricing", checkout_option: "trial"})) {
        trackedOpen.current = location.key;
      }
    };
    trackOpen();
    window.addEventListener("lgn:cookie-consent", trackOpen);
    return () => window.removeEventListener("lgn:cookie-consent", trackOpen);
  }, [checkoutOption, location.key]);

  if (!checkoutOption) return null;

  return (
    <dialog
      ref={dialog}
      className="pricing-payment-dialog"
      aria-label={checkoutOption === "trial" ? "Оплата пробного доступа" : "Оплата месячной подписки"}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <button type="button" className="pricing-payment-close" aria-label="Закрыть оплату" onClick={close}>×</button>
      <SubscribePage checkoutOption={checkoutOption}/>
    </dialog>
  );
}

if (triggers.length && container) {
  const queryClient = new QueryClient({
    defaultOptions: {queries: {retry: 1, refetchOnWindowFocus: false}},
  });
  const shadow = container.attachShadow({mode: "open"});
  createRoot(shadow).render(
    <StrictMode>
      <style>{subscribeStyles.replace(":root", ":host")}</style>
      <style>{dialogStyles}</style>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PricingCheckout/>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}
